"use client";

import { forwardRef } from "react";
import { ArrowButton } from "@/components/molecules/ListingCardButtons";
import { cn } from "@/lib/utils";
import type { RotationalCarouselButtonProps } from "./RotationalCarousel.types";
import { useRotationalCarousel } from "./RotationalCarouselContext";

/**
 * Previous-slide navigation button for `<RotationalCarousel>`.
 *
 * @example
 * ```tsx
 * <RotationalCarousel>
 *   <RotationalCarouselContent>...</RotationalCarouselContent>
 *   <RotationalCarouselPrevious />
 * </RotationalCarousel>
 * ```
 */
const RotationalCarouselPrevious = forwardRef<
  HTMLButtonElement,
  RotationalCarouselButtonProps
>(({ className }, ref) => {
  const { goPrev } = useRotationalCarousel();
  return (
    <ArrowButton
      ref={ref}
      aria-label="Previous slide"
      className={cn(
        // Centered vertically over the content area; left-side offset matches
        // the right-side offset on RotationalCarouselNext for visual symmetry.
        "tw-absolute tw-left-[5%] tw-top-1/2 tw--translate-y-1/2 tw-z-20",
        className,
      )}
      onClick={goPrev}
    />
  );
});

RotationalCarouselPrevious.displayName = "RotationalCarouselPrevious";

/**
 * Next-slide navigation button for `<RotationalCarousel>`.
 *
 * @example
 * ```tsx
 * <RotationalCarousel>
 *   <RotationalCarouselContent>...</RotationalCarouselContent>
 *   <RotationalCarouselNext />
 * </RotationalCarousel>
 * ```
 */
const RotationalCarouselNext = forwardRef<
  HTMLButtonElement,
  RotationalCarouselButtonProps
>(({ className }, ref) => {
  const { goNext } = useRotationalCarousel();
  return (
    <ArrowButton
      ref={ref}
      right
      aria-label="Next slide"
      className={cn(
        // Centered vertically over the content area; right-side offset mirrors
        // the left-side offset on RotationalCarouselPrevious for visual symmetry.
        "tw-absolute tw-right-[5%] tw-top-1/2 tw--translate-y-1/2 tw-z-20",
        className,
      )}
      onClick={goNext}
    />
  );
});

RotationalCarouselNext.displayName = "RotationalCarouselNext";

export { RotationalCarouselPrevious, RotationalCarouselNext };
